import React, { useState, useEffect } from 'react';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import SearchIcon from "@mui/icons-material/Search";
import InputBase from "@mui/material/InputBase";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import Alert from "@mui/material/Alert";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DialogActions from "@mui/material/DialogActions";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SecurityIcon from "@mui/icons-material/Security";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { addSubUser, getAllSubUsers, updateSubUserStatus, updateSubUser } from '../../api/subUserApi';
import { useAuth } from '../../context/AuthContext';
import LoadingGate from '../LoadingGate';
import GroupIcon from '@mui/icons-material/Group';
import "../../styles.css";

const ACCESS_LEVELS = [
  { value: "full_access", label: "Full Access", color: "#059669" },
  { value: "view_only", label: "View Only", color: "#d97706" },
  { value: "no_access", label: "No Access", color: "#d92d20" },
];

// Sub-permissions under Vendor Management Tab Access can never exceed their
// parent's level — full 3 options when the parent is Full Access, capped to
// View/No Access when the parent is View Only.
const CHILD_ACCESS_LEVELS = ACCESS_LEVELS.filter((l) => l.value !== "full_access");

// Transactions Visibility — a scope choice, not an access level, so it gets
// its own two-option control with its own colors.
const SCOPE_LEVELS = [
  { value: "all", label: "All Transactions", color: "#1565C0" },
  { value: "own", label: "Only Belongs to Them", color: "#7c3aed" },
];

const VENDOR_RIGHTS_DEFAULTS = {
  vendorTransactionScope: "own",
  vendorListTab: "no_access",
  approvalMatrixTab: "no_access",
  vendorManagementTab: "no_access",
  vendorPaymentRequesting: "no_access",
  vendorTabPending: "no_access",
  vendorTabApproved: "no_access",
  vendorTabPaid: "no_access",
  vendorTabCorrection: "no_access",
  vendorTabRejected: "no_access",
  vendorTabAll: "no_access",
  // Defaults to view_only (not no_access like its siblings) — everyone can
  // already browse this cross-project view; only the bulk Remove/Reject
  // action itself needs an explicit full_access grant.
  vendorTabVendorWise: "view_only",
  vendorTabProjectWise: "view_only",
};

const VENDOR_MANAGEMENT_SUB_FIELDS = [
  "vendorPaymentRequesting",
  "vendorTabPending",
  "vendorTabApproved",
  "vendorTabPaid",
  "vendorTabCorrection",
  "vendorTabRejected",
  "vendorTabAll",
  "vendorTabVendorWise",
  "vendorTabProjectWise",
];

const Users = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  // Pagination — cap the visible list at 20, "Load More" reveals the rest
  const PAGE_SIZE = 20;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [accessRightsDialogOpen, setAccessRightsDialogOpen] = useState(false);
  const [accessRights, setAccessRights] = useState({
    summaryTab: "no_access",
    allClientsTab: "no_access",
    basicDetailsTab: "no_access",
    clientSummaryTab: "no_access",
    clientOverviewTab: "no_access",
    deleteClient: "no_access",
    downloadClientBalanceSheet: "no_access",
    incomeDetailsExpandable: "no_access",
    initialContractValueBreakupTable: "no_access",
    additionalWorkValueBreakup: "no_access",
    expensesDetailsExpandable: "no_access",
    materialPurchasesTable: "no_access",
    labourCharges: "no_access",
    ...VENDOR_RIGHTS_DEFAULTS
  });
  const [formData, setFormData] = useState({
    username: "",
    designation: "",
    email: "",
    mobile: "",
    access_rights: ""
  });

  useEffect(() => {
    fetchUsers();
  }, [tabValue, user]);

  // Reset pagination whenever the tab or search changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [tabValue, searchQuery]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      if (!user?.company_id) return;

      const params = {
        company_id: user.company_id,
        user_status: tabValue === 0 ? 'active' : 'inactive'
      };

      const response = await getAllSubUsers(params);
      if (response.success) {
        setUsers(response.data);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleAddUserClick = () => {
    setAddUserDialogOpen(true);
  };

  const handleAddUserClose = () => {
    setAddUserDialogOpen(false);
    setError(null);
    setFieldErrors({});
    setFormData({
      username: "",
      designation: "",
      email: "",
      mobile: "",
      access_rights: ""
    });
    setAccessRights({
      summaryTab: "no_access",
      allClientsTab: "no_access",
      basicDetailsTab: "no_access",
      clientSummaryTab: "no_access",
      clientOverviewTab: "no_access",
      deleteClient: "no_access",
      downloadClientBalanceSheet: "no_access",
      incomeDetailsExpandable: "no_access",
      initialContractValueBreakupTable: "no_access",
      additionalWorkValueBreakup: "no_access",
      expensesDetailsExpandable: "no_access",
      materialPurchasesTable: "no_access",
      labourCharges: "no_access",
      ...VENDOR_RIGHTS_DEFAULTS
    });
  };

  const handleAddUser = async () => {
    try {
      setError(null);
      setFieldErrors({});

      // Send accessRights object directly (no JSON.stringify)
      const dataToSend = {
        ...formData,
        access_rights: accessRights
      };

      const response = await addSubUser(dataToSend);
      if (response.success) {
        handleAddUserClose();
        fetchUsers();
      }
    } catch (error) {
      console.error("Error adding user:", error);
      const errorMessage = error.message || "Error adding user";
      const errorField = error.field;

      // If backend sends field information, use it directly
      if (errorField) {
        setFieldErrors({ [errorField]: errorMessage });
      }
      // Otherwise, parse error message to determine which field has the issue
      else if (errorMessage.toLowerCase().includes('email')) {
        setFieldErrors({ email: errorMessage });
      } else if (errorMessage.toLowerCase().includes('mobile')) {
        setFieldErrors({ mobile: errorMessage });
      } else {
        setError(errorMessage);
      }
    }
  };

  const handleEditUserClick = (subUser) => {
    setEditingUser(subUser);
    setFormData({
      username: subUser.fullname,
      designation: subUser.designation,
      email: subUser.email,
      mobile: subUser.mobile,
      access_rights: subUser.access_rights
    });

    // Load access_rights object directly for editing — merged over defaults so
    // users saved before Vendor Rights existed don't leave those fields undefined
    if (subUser.access_rights) {
      setAccessRights({ ...VENDOR_RIGHTS_DEFAULTS, ...subUser.access_rights });
    }

    setEditUserDialogOpen(true);
  };

  const handleEditUserClose = () => {
    setEditUserDialogOpen(false);
    setEditingUser(null);
    setError(null);
    setFieldErrors({});
    setFormData({
      username: "",
      designation: "",
      email: "",
      mobile: "",
      access_rights: ""
    });
    setAccessRights({
      summaryTab: "no_access",
      allClientsTab: "no_access",
      basicDetailsTab: "no_access",
      clientSummaryTab: "no_access",
      clientOverviewTab: "no_access",
      deleteClient: "no_access",
      downloadClientBalanceSheet: "no_access",
      incomeDetailsExpandable: "no_access",
      initialContractValueBreakupTable: "no_access",
      additionalWorkValueBreakup: "no_access",
      expensesDetailsExpandable: "no_access",
      materialPurchasesTable: "no_access",
      labourCharges: "no_access",
      ...VENDOR_RIGHTS_DEFAULTS
    });
  };

  const handleUpdateUser = async () => {
    try {
      setError(null);
      setFieldErrors({});

      // Send accessRights object directly (no JSON.stringify)
      const dataToSend = {
        ...formData,
        access_rights: accessRights
      };

      const response = await updateSubUser(editingUser._id, dataToSend);
      if (response.success) {
        handleEditUserClose();
        fetchUsers();
      }
    } catch (error) {
      console.error("Error updating user:", error);
      const errorMessage = error.message || "Error updating user";
      const errorField = error.field;

      // If backend sends field information, use it directly
      if (errorField) {
        setFieldErrors({ [errorField]: errorMessage });
      }
      // Otherwise, parse error message to determine which field has the issue
      else if (errorMessage.toLowerCase().includes('email')) {
        setFieldErrors({ email: errorMessage });
      } else if (errorMessage.toLowerCase().includes('mobile')) {
        setFieldErrors({ mobile: errorMessage });
      } else {
        setError(errorMessage);
      }
    }
  };

  const handleStatusChange = async (userId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      const response = await updateSubUserStatus(userId, newStatus);
      if (response.success) {
        fetchUsers();
      }
    } catch (error) {
      console.error("Error updating user status:", error);
    }
  };

  const handleInputChange = (field, value) => {
    let processedValue = value;

    // Capitalize first letter of each word for username and designation
    if (field === 'username' || field === 'designation') {
      processedValue = value
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    }

    setFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));
  };

  // A stunning tappable-card access-level row — replaces the old RadioGroup pattern.
  // `levels` lets callers pass a restricted set (e.g. Vendor Management's children
  // drop "Full Access" once their parent is capped to View Only). `onSelect` lets a
  // parent row run cascading logic instead of the plain field setter.
  const renderAccessRow = (fieldKey, label, levels = ACCESS_LEVELS, onSelect) => (
    <Box sx={{ mb: 2, pb: 2, borderBottom: "1px solid #f0f2f5" }}>
      <Typography sx={{ fontSize: "13px", fontWeight: 600, fontFamily: "Roboto, Arial, sans-serif", color: "#101828", mb: 1 }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        {levels.map((lvl) => {
          const selected = accessRights[fieldKey] === lvl.value;
          return (
            <Box
              key={lvl.value}
              onClick={() => (onSelect ? onSelect(lvl.value) : setAccessRights(prev => ({ ...prev, [fieldKey]: lvl.value })))}
              sx={{
                flex: "1 1 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "5px",
                textAlign: "center",
                py: 1,
                px: 1.5,
                borderRadius: "10px",
                border: `1.5px solid ${selected ? lvl.color : "#e4e7ec"}`,
                background: selected ? lvl.color + "18" : "#fff",
                color: selected ? lvl.color : "#667085",
                fontWeight: selected ? 700 : 500,
                fontSize: "12.5px",
                fontFamily: "Roboto, Arial, sans-serif",
                cursor: "pointer",
                whiteSpace: "nowrap",
                transition: "all 0.15s ease",
                "&:active": { transform: "scale(0.97)" },
              }}
            >
              {selected && <CheckCircleIcon sx={{ fontSize: 14 }} />}
              {lvl.label}
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  // Vendor Management's sub-permissions (Payment Requesting + the 6 status
  // tabs) can never grant more than their parent — picking View Only here
  // clamps any child already set to Full Access down to View Only, and
  // picking No Access resets every child back to No Access so nothing stale
  // survives hidden in the background.
  const handleVendorManagementTabChange = (value) => {
    setAccessRights(prev => {
      const next = { ...prev, vendorManagementTab: value };
      if (value === "no_access") {
        VENDOR_MANAGEMENT_SUB_FIELDS.forEach((f) => { next[f] = "no_access"; });
      } else if (value === "view_only") {
        VENDOR_MANAGEMENT_SUB_FIELDS.forEach((f) => {
          if (next[f] === "full_access") next[f] = "view_only";
        });
      }
      return next;
    });
  };

  return (
    <div className="client-container">
      {loading && <LoadingGate icon={GroupIcon} message="Loading users…" />}

      {isMobile ? (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
            <Typography sx={{ fontSize: "20px", fontWeight: 800, color: "#101828", fontFamily: "Roboto, Arial, sans-serif" }}>Users</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, background: "#fff", border: "1px solid #eaecf0", borderRadius: "14px", px: 1.5, boxShadow: "0 1px 2px rgba(16,24,40,0.04)" }}>
              <SearchIcon sx={{ fontSize: 20, color: "#98a2b3" }} />
              <InputBase
                placeholder="Search users…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ flex: 1, fontSize: "14px", py: 1.25, fontFamily: "Roboto, Arial, sans-serif" }}
              />
            </Box>
          </Box>

          <div className="arcdx-v-pills" style={{ marginBottom: 14 }}>
            <div className={`arcdx-v-pill${tabValue === 0 ? " active" : ""}`} onClick={() => setTabValue(0)}>Active Users</div>
            <div className={`arcdx-v-pill${tabValue === 1 ? " active" : ""}`} onClick={() => setTabValue(1)}>In-Active Users</div>
          </div>

          {(() => {
            const filteredUsers = users.filter(subUser => subUser.fullname?.toLowerCase().includes(searchQuery.toLowerCase()));
            const visibleUsers = filteredUsers.slice(0, visibleCount);
            const hasMore = filteredUsers.length > visibleCount;
            if (filteredUsers.length === 0) {
              return (
                <Box sx={{ textAlign: "center", py: 6, color: "#98a2b3" }}>
                  <Typography sx={{ fontSize: "14px", fontFamily: "Roboto, Arial, sans-serif" }}>No users found</Typography>
                </Box>
              );
            }
            return (
              <>
                {visibleUsers.map((subUser) => (
                  <Box
                    key={subUser._id}
                    sx={{ background: "#fff", borderRadius: "16px", p: 2, mb: 1.25, boxShadow: "0 2px 10px rgba(16,24,40,0.06)", border: "1px solid #f0f1f3", display: "flex", alignItems: "center", gap: 1.5 }}
                  >
                    <Box sx={{ width: 40, height: 40, borderRadius: "12px", background: "#eff5ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Typography sx={{ fontSize: "15px", fontWeight: 800, color: "#1f6fd6" }}>{(subUser.fullname || "U").charAt(0).toUpperCase()}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: "14.5px", fontWeight: 700, color: "#101828", fontFamily: "Roboto, Arial, sans-serif" }}>{subUser.fullname}</Typography>
                      {subUser.designation && <Typography noWrap sx={{ fontSize: "11.5px", color: "#98a2b3", fontWeight: 600, fontFamily: "Roboto, Arial, sans-serif" }}>{subUser.designation}</Typography>}
                    </Box>
                    <IconButton onClick={() => handleEditUserClick(subUser)} sx={{ background: "#eff4ff", color: "#1E40AF", width: 34, height: 34 }}>
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <Switch
                      checked={subUser.user_status === 'active'}
                      onChange={() => handleStatusChange(subUser._id, subUser.user_status)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#39aafc' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#39aafc' } }}
                    />
                  </Box>
                ))}
                {hasMore && (
                  <Box
                    onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                    sx={{ textAlign: "center", py: 1.4, mt: 0.5, mb: 2, borderRadius: "14px", border: "1.5px dashed #b8dffb", color: "#1565C0", fontWeight: 700, fontSize: "13.5px", cursor: "pointer" }}
                  >
                    Load More ({filteredUsers.length - visibleUsers.length} remaining)
                  </Box>
                )}
              </>
            );
          })()}

          {/* Add User FAB */}
          <Box
            onClick={handleAddUserClick}
            sx={{ position: "fixed", right: "20px", bottom: "calc(96px + env(safe-area-inset-bottom))", zIndex: 1200, width: 56, height: 56, borderRadius: "18px", background: "linear-gradient(135deg, #39aafc 0%, #1f8fe8 100%)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 10px 24px rgba(31,143,232,0.5)", cursor: "pointer", transition: "transform 0.15s ease", "&:active": { transform: "scale(0.92)" } }}
          >
            <PersonAddAlt1Icon sx={{ fontSize: 26 }} />
          </Box>
        </>
      ) : (
        <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          marginBottom: "20px",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: "10.5px", fontWeight: 700, letterSpacing: "0.12em", color: "#98a2b3", textTransform: "uppercase", fontFamily: "Roboto, Arial, sans-serif" }}>
            Team
          </Typography>
          <Typography sx={{ fontSize: "18px", fontWeight: 800, color: "#101828", letterSpacing: "-0.01em", fontFamily: "Roboto, Arial, sans-serif" }}>
            Users
          </Typography>
        </Box>
      </Box>

      {/* Segmented status control (same tabValue + handleTabChange) */}
      <Box
        sx={{
          display: "inline-flex",
          gap: "4px",
          background: "#f2f4f7",
          borderRadius: "13px",
          p: "4px",
          marginBottom: "20px",
        }}
      >
        {["Active Users", "In-Active Users"].map((label, idx) => {
          const active = tabValue === idx;
          return (
            <Box
              key={label}
              onClick={(e) => handleTabChange(e, idx)}
              sx={{
                px: 2.25,
                py: "8px",
                borderRadius: "10px",
                fontSize: "13px",
                fontWeight: active ? 700 : 600,
                fontFamily: "Roboto, Arial, sans-serif",
                color: active ? "#1565C0" : "#667085",
                background: active ? "#ffffff" : "transparent",
                boxShadow: active ? "0 1px 3px rgba(16,24,40,0.10)" : "none",
                cursor: "pointer",
                transition: "all 0.2s ease",
                userSelect: "none",
                whiteSpace: "nowrap",
                "&:hover": { color: "#1565C0" },
              }}
            >
              {label}
            </Box>
          );
        })}
      </Box>

      {/* Users Table — table-card pattern: the table owns its header zone */}
      <Box
        sx={{
          background: "#ffffff",
          border: "1px solid #e9eef5",
          borderRadius: "18px",
          boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 8px 24px rgba(13,43,92,0.05)",
          overflow: "hidden",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: "16px 20px", borderBottom: "1px solid #eef2f7" }}>
          <Typography sx={{ fontSize: "15.5px", fontWeight: 800, color: "#101828", letterSpacing: "-0.01em", fontFamily: "Roboto, Arial, sans-serif" }}>
            {tabValue === 0 ? "Active Members" : "In-Active Members"}
          </Typography>
          <Box sx={{ px: 1.25, py: "3px", borderRadius: "999px", background: "rgba(57,170,252,0.10)", color: "#1565C0", fontSize: "11.5px", fontWeight: 800, fontFamily: "Roboto, Arial, sans-serif" }}>
            {users.filter(subUser => subUser.fullname?.toLowerCase().includes(searchQuery.toLowerCase())).length}
          </Box>
          <Box sx={{ flex: 1 }} />
          <Box className="client-search-bar" sx={{ width: "230px !important" }}>
            <SearchIcon className="search-icon-wrapper" />
            <InputBase
              className="styled-input-base"
              placeholder="Search users…"
              inputProps={{ "aria-label": "search" }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Box>
          <Button
            variant="contained"
            onClick={handleAddUserClick}
            startIcon={<PersonAddAlt1Icon sx={{ fontSize: 18 }} />}
            sx={{
              background: "linear-gradient(135deg, #4db2fc, #1565C0)",
              color: "white",
              textTransform: "none",
              fontSize: "13px",
              fontFamily: "Roboto, sans-serif",
              fontWeight: 700,
              minWidth: "126px",
              height: "38px",
              borderRadius: "12px",
              boxShadow: "0 8px 20px rgba(21, 101, 192, 0.28)",
              "&:hover": {
                background: "linear-gradient(135deg, #39aafc, #0d47a1)",
                boxShadow: "0 10px 24px rgba(21, 101, 192, 0.38)",
              },
            }}
          >
            Add User
          </Button>
        </Box>
        <TableContainer>
          <Table sx={{ minWidth: 650, "& tbody tr:hover": { backgroundColor: "#f8fbff" } }} aria-label="users table">
            <TableHead>
              <TableRow>
                <TableCell className="table-head-cell" sx={{ width: "34%", paddingLeft: "20px !important" }}>
                  Member
                </TableCell>
                <TableCell className="table-head-cell" sx={{ width: "26%" }}>
                  Designation
                </TableCell>
                <TableCell className="table-head-cell" sx={{ width: "26%" }}>
                  Email
                </TableCell>
                <TableCell className="table-head-cell" sx={{ width: "14%", textAlign: "center", paddingRight: "20px !important" }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                const filteredUsers = users.filter(subUser => subUser.fullname?.toLowerCase().includes(searchQuery.toLowerCase()));
                const visibleUsers = filteredUsers.slice(0, visibleCount);
                return visibleUsers.map((subUser, index) => (
                <TableRow key={subUser._id}>
                  <TableCell className="table-body-cell" sx={{ paddingLeft: "20px !important", paddingTop: "11px", paddingBottom: "11px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                      <Box
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: "12px",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "14.5px",
                          fontWeight: 800,
                          color: "#1565C0",
                          background: "rgba(57, 170, 252, 0.12)",
                        }}
                      >
                        {(subUser.fullname || "U").charAt(0).toUpperCase()}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: "13.5px", fontWeight: 700, color: "#101828", fontFamily: "Roboto, Arial, sans-serif", lineHeight: 1.3 }}>
                          {subUser.fullname}
                        </Typography>
                        <Typography noWrap sx={{ fontSize: "11px", color: "#9aa7b8", fontFamily: "Roboto, Arial, sans-serif", mt: "2px" }}>
                          {subUser.user_status === 'active' ? "Active member" : "In-active"}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell className="table-body-cell" sx={{ color: "#667085" }}>
                    {subUser.designation || "—"}
                  </TableCell>
                  <TableCell className="table-body-cell" sx={{ color: "#667085" }}>
                    {subUser.email || "—"}
                  </TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "center" }}>
                    <IconButton
                      onClick={() => handleEditUserClick(subUser)}
                      sx={{ padding: "4px", marginRight: "8px", color: "#98a2b3", "&:hover": { color: "#1f6fd6" } }}
                    >
                      <EditIcon sx={{ fontSize: "18px" }} />
                    </IconButton>
                    <Tooltip title={subUser.user_status === 'active' ? "Make User In-Active" : "Make User Active"}>
                      <Switch
                        checked={subUser.user_status === 'active'}
                        onChange={() => handleStatusChange(subUser._id, subUser.user_status)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#39aafc',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#39aafc',
                          },
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                </TableRow>
                ));
              })()}
              {(() => {
                const filteredUsers = users.filter(subUser => subUser.fullname?.toLowerCase().includes(searchQuery.toLowerCase()));
                return filteredUsers.length > visibleCount && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: "center", padding: "14px" }}>
                      <Box
                        onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                        sx={{ display: "inline-block", cursor: "pointer", borderRadius: "999px", border: "1.5px dashed #b8dffb", color: "#1565C0", fontWeight: 700, fontSize: "13.5px", px: 2.5, py: 1 }}
                      >
                        Load More ({filteredUsers.length - visibleCount} remaining)
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })()}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
        </>
      )}

      {/* Add User Dialog */}
      <Dialog
        open={addUserDialogOpen}
        onClose={handleAddUserClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: isMobile ? {} : { borderRadius: "24px", overflow: "hidden" } }}
      >
        <Box sx={{ background: "linear-gradient(155deg, #4db2fc 0%, #1565C0 100%)", pt: "max(24px, env(safe-area-inset-top))", pb: 2.5, px: 3, color: "#fff", textAlign: "center", flexShrink: 0 }}>
          <Box sx={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px" }}>
            <PersonAddAlt1Icon sx={{ fontSize: 24 }} />
          </Box>
          <Typography sx={{ fontSize: "18px", fontWeight: 800, fontFamily: "Roboto, Arial, sans-serif" }}>
            Add User
          </Typography>
        </Box>
        <DialogContent sx={{ paddingTop: "20px !important", display: "flex", flexDirection: "column", alignItems: "center" }}>
          {error && (
            <Alert severity="error" sx={{ width: "100%", marginBottom: "20px" }}>
              {error}
            </Alert>
          )}

          <TextField
            label="User Name"
            variant="outlined"
            fullWidth
            value={formData.username}
            onChange={(e) => handleInputChange('username', e.target.value)}
            sx={{
              marginBottom: "20px",
              "& .MuiInputLabel-root": {
                fontSize: "13px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
              "& .MuiOutlinedInput-root": {
                fontSize: "13px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
            }}
          />

          <TextField
            label="Designation"
            variant="outlined"
            fullWidth
            value={formData.designation}
            onChange={(e) => handleInputChange('designation', e.target.value)}
            sx={{
              marginBottom: "20px",
              "& .MuiInputLabel-root": {
                fontSize: "13px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
              "& .MuiOutlinedInput-root": {
                fontSize: "13px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
            }}
          />

          <TextField
            label="Email"
            variant="outlined"
            fullWidth
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            error={!!fieldErrors.email}
            helperText={fieldErrors.email}
            sx={{
              marginBottom: "20px",
              "& .MuiInputLabel-root": {
                fontSize: "13px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
              "& .MuiOutlinedInput-root": {
                fontSize: "13px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
              "& .MuiFormHelperText-root": {
                fontSize: "12px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
            }}
          />

          <TextField
            label="Mobile"
            variant="outlined"
            fullWidth
            value={formData.mobile}
            onChange={(e) => handleInputChange('mobile', e.target.value)}
            error={!!fieldErrors.mobile}
            helperText={fieldErrors.mobile}
            sx={{
              marginBottom: "20px",
              "& .MuiInputLabel-root": {
                fontSize: "13px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
              "& .MuiOutlinedInput-root": {
                fontSize: "13px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
              "& .MuiFormHelperText-root": {
                fontSize: "12px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
            }}
          />

          <Box
            onClick={() => setAccessRightsDialogOpen(true)}
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 1,
              background: "#eff5ff",
              borderRadius: "12px",
              px: 2,
              py: 1.5,
              marginBottom: "20px",
              cursor: "pointer",
              "&:active": { background: "#e2effe" },
            }}
          >
            <SecurityIcon sx={{ fontSize: 18, color: "#1565C0" }} />
            <Typography sx={{ fontSize: "13.5px", fontWeight: 700, fontFamily: "Roboto, Arial, sans-serif", color: "#1565C0", flex: 1 }}>
              Access Rights
            </Typography>
            <Typography sx={{ fontSize: "12px", fontFamily: "Roboto, Arial, sans-serif", color: "#98a2b3" }}>Configure ›</Typography>
          </Box>

          <Button
            onClick={handleAddUser}
            fullWidth
            sx={{
              textTransform: "none",
              fontWeight: 800,
              fontSize: "14px",
              color: "#fff",
              background: "linear-gradient(135deg,#4db2fc,#1565C0)",
              borderRadius: "14px",
              py: 1.2,
              boxShadow: "0 8px 20px rgba(21,101,192,0.35)",
              "&:hover": { background: "linear-gradient(135deg,#39aafc,#0d47a1)" },
            }}
          >
            Add User
          </Button>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={editUserDialogOpen}
        onClose={handleEditUserClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: isMobile ? {} : { borderRadius: "24px", overflow: "hidden" } }}
      >
        <Box sx={{ background: "linear-gradient(155deg, #4db2fc 0%, #1565C0 100%)", pt: "max(24px, env(safe-area-inset-top))", pb: 2.5, px: 3, color: "#fff", textAlign: "center", flexShrink: 0 }}>
          <Box sx={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px" }}>
            <EditIcon sx={{ fontSize: 22 }} />
          </Box>
          <Typography sx={{ fontSize: "18px", fontWeight: 800, fontFamily: "Roboto, Arial, sans-serif" }}>
            Edit User
          </Typography>
        </Box>
        <DialogContent sx={{ paddingTop: "20px !important", display: "flex", flexDirection: "column", alignItems: "center" }}>
          {error && (
            <Alert severity="error" sx={{ width: "100%", marginBottom: "20px" }}>
              {error}
            </Alert>
          )}

          <TextField
            label="User Name"
            variant="outlined"
            fullWidth
            value={formData.username}
            onChange={(e) => handleInputChange('username', e.target.value)}
            sx={{
              marginBottom: "20px",
              "& .MuiInputLabel-root": {
                fontSize: "13px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
              "& .MuiOutlinedInput-root": {
                fontSize: "13px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
            }}
          />

          <TextField
            label="Designation"
            variant="outlined"
            fullWidth
            value={formData.designation}
            onChange={(e) => handleInputChange('designation', e.target.value)}
            sx={{
              marginBottom: "20px",
              "& .MuiInputLabel-root": {
                fontSize: "13px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
              "& .MuiOutlinedInput-root": {
                fontSize: "13px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
            }}
          />

          <TextField
            label="Email"
            variant="outlined"
            fullWidth
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            disabled={editingUser?.emailVerified}
            error={!!fieldErrors.email}
            helperText={fieldErrors.email || (editingUser?.emailVerified ? "Email cannot be changed for verified users" : "")}
            sx={{
              marginBottom: "20px",
              "& .MuiInputLabel-root": {
                fontSize: "13px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
              "& .MuiOutlinedInput-root": {
                fontSize: "13px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
              "& .MuiFormHelperText-root": {
                fontSize: "12px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
            }}
          />

          <TextField
            label="Mobile"
            variant="outlined"
            fullWidth
            value={formData.mobile}
            onChange={(e) => handleInputChange('mobile', e.target.value)}
            error={!!fieldErrors.mobile}
            helperText={fieldErrors.mobile}
            sx={{
              marginBottom: "20px",
              "& .MuiInputLabel-root": {
                fontSize: "13px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
              "& .MuiOutlinedInput-root": {
                fontSize: "13px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
              "& .MuiFormHelperText-root": {
                fontSize: "12px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
            }}
          />

          <Box
            onClick={() => setAccessRightsDialogOpen(true)}
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 1,
              background: "#eff5ff",
              borderRadius: "12px",
              px: 2,
              py: 1.5,
              marginBottom: "20px",
              cursor: "pointer",
              "&:active": { background: "#e2effe" },
            }}
          >
            <SecurityIcon sx={{ fontSize: 18, color: "#1565C0" }} />
            <Typography sx={{ fontSize: "13.5px", fontWeight: 700, fontFamily: "Roboto, Arial, sans-serif", color: "#1565C0", flex: 1 }}>
              Access Rights
            </Typography>
            <Typography sx={{ fontSize: "12px", fontFamily: "Roboto, Arial, sans-serif", color: "#98a2b3" }}>Configure ›</Typography>
          </Box>

          <Button
            onClick={handleUpdateUser}
            fullWidth
            sx={{
              textTransform: "none",
              fontWeight: 800,
              fontSize: "14px",
              color: "#fff",
              background: "linear-gradient(135deg,#4db2fc,#1565C0)",
              borderRadius: "14px",
              py: 1.2,
              boxShadow: "0 8px 20px rgba(21,101,192,0.35)",
              "&:hover": { background: "linear-gradient(135deg,#39aafc,#0d47a1)" },
            }}
          >
            Update
          </Button>
        </DialogContent>
      </Dialog>

      {/* Access Rights Dialog */}
      <Dialog
        open={accessRightsDialogOpen}
        onClose={() => setAccessRightsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: isMobile ? {} : { borderRadius: "24px", overflow: "hidden", height: "80vh" } }}
      >
        <Box sx={{ background: "linear-gradient(155deg, #4db2fc 0%, #1565C0 100%)", pt: "max(20px, env(safe-area-inset-top))", pb: 2.5, px: 3, color: "#fff", textAlign: "center", flexShrink: 0 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px" }}>
            <SecurityIcon sx={{ fontSize: 22 }} />
          </Box>
          <Typography sx={{ fontSize: "17px", fontWeight: 800, fontFamily: "Roboto, Arial, sans-serif" }}>
            Access Rights
          </Typography>
        </Box>
        <DialogContent sx={{ paddingTop: "16px !important", display: "flex", flexDirection: "column" }}>
          {/* Access Rights Content */}
          <Box sx={{ flex: 1, overflowY: "auto", padding: "0 4px" }}>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: "#f5f5f5",
                  "& .MuiAccordionSummary-content": {
                    margin: "12px 0",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 600,
                    fontFamily: "Roboto, Arial, sans-serif",
                    color: "#333",
                  }}
                >
                  Client Rights
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: "16px" }}>
                {renderAccessRow("summaryTab", "Summary Tab")}
                {renderAccessRow("allClientsTab", "All Clients Tab")}

                {/* Client Balance Sheet Header */}
                <Box
                  sx={{
                    marginBottom: "16px",
                    paddingBottom: "8px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 600,
                      fontFamily: "Roboto, Arial, sans-serif",
                      color: "#333",
                      textDecoration: "underline",
                    }}
                  >
                    Client Balance Sheet
                  </Typography>
                </Box>

                {renderAccessRow("basicDetailsTab", "Basic Details Tab")}
                {renderAccessRow("clientSummaryTab", "Client Summary Tab")}

                {renderAccessRow("clientOverviewTab", "Client Overview Tab")}
                {renderAccessRow("deleteClient", "Delete Client")}

                {renderAccessRow("downloadClientBalanceSheet", "Download Client Balance Sheet")}

                {/* Client Summary Deep Access Header */}
                <Box
                  sx={{
                    marginBottom: "16px",
                    paddingBottom: "8px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 600,
                      fontFamily: "Roboto, Arial, sans-serif",
                      color: "#333",
                      textDecoration: "underline",
                    }}
                  >
                    Client Summary Deep Access
                  </Typography>
                </Box>

                {renderAccessRow("incomeDetailsExpandable", "Income Details Expandable")}
                {renderAccessRow("initialContractValueBreakupTable", "Initial Contract Value Breakup Table")}

                {renderAccessRow("additionalWorkValueBreakup", "Additional Work Value Breakup")}
                {renderAccessRow("expensesDetailsExpandable", "Expenses Details Expandable")}

                {renderAccessRow("materialPurchasesTable", "Material Purchases Table")}
                {renderAccessRow("labourCharges", "Labour Charges")}
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: "#f5f5f5",
                  "& .MuiAccordionSummary-content": {
                    margin: "12px 0",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 600,
                    fontFamily: "Roboto, Arial, sans-serif",
                    color: "#333",
                  }}
                >
                  Vendor Rights
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: "16px" }}>
                {renderAccessRow("vendorTransactionScope", "Transactions Visibility", SCOPE_LEVELS)}
                {renderAccessRow("vendorListTab", "Vendor List Tab")}
                {renderAccessRow("approvalMatrixTab", "Approval Matrix Tab")}
                {renderAccessRow("vendorManagementTab", "Vendor Management Tab Access", ACCESS_LEVELS, handleVendorManagementTabChange)}

                {accessRights.vendorManagementTab !== "no_access" && (() => {
                  const childLevels = accessRights.vendorManagementTab === "full_access" ? ACCESS_LEVELS : CHILD_ACCESS_LEVELS;
                  return (
                    <>
                      <Box sx={{ marginBottom: "16px", paddingBottom: "8px" }}>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            fontFamily: "Roboto, Arial, sans-serif",
                            color: "#333",
                            textDecoration: "underline",
                          }}
                        >
                          Vendor Management Permissions
                        </Typography>
                      </Box>

                      {renderAccessRow("vendorPaymentRequesting", "Payment Requesting", childLevels)}

                      <Box sx={{ marginBottom: "16px", paddingBottom: "8px" }}>
                        <Typography
                          sx={{
                            fontSize: "12.5px",
                            fontWeight: 600,
                            fontFamily: "Roboto, Arial, sans-serif",
                            color: "#667085",
                          }}
                        >
                          Tabs Access
                        </Typography>
                      </Box>

                      {renderAccessRow("vendorTabPending", "Pending", childLevels)}
                      {renderAccessRow("vendorTabApproved", "Approved", childLevels)}
                      {renderAccessRow("vendorTabPaid", "Paid", childLevels)}
                      {renderAccessRow("vendorTabCorrection", "Correction", childLevels)}
                      {renderAccessRow("vendorTabRejected", "Rejected", childLevels)}
                      {renderAccessRow("vendorTabVendorWise", "Vendor-Wise", childLevels)}
                      {renderAccessRow("vendorTabProjectWise", "Project-Wise", childLevels)}
                      {renderAccessRow("vendorTabAll", "All", childLevels)}
                    </>
                  );
                })()}
              </AccordionDetails>
            </Accordion>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: "max(16px, env(safe-area-inset-bottom))", pt: 1 }}>
          <Button
            onClick={() => setAccessRightsDialogOpen(false)}
            fullWidth
            sx={{ textTransform: "none", fontWeight: 800, fontSize: "14px", color: "#fff", background: "linear-gradient(135deg,#4db2fc,#1565C0)", borderRadius: "14px", py: 1.2, boxShadow: "0 8px 20px rgba(21,101,192,0.35)", "&:hover": { background: "linear-gradient(135deg,#39aafc,#0d47a1)" } }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Users;
